import { Box, Button, Field, Form, Grid, Select, Typography } from "components";
import { EnumEmployeeDelegationMode } from "constants/enums/employee-delegation-mode";
import { PERMISSIONS } from "constants/enums/permissions";
import { compose, withAuthorize, withHooks, withTranslation } from "enhancers";
import { gql } from "hooks";
import paths from "routes/paths";

const EmployeeDelegationFormComponent = ({
  t,
  handleCancel,
  enabledSubmitButton,
  employeeOptions,
  targetOptions,
  isEnableSelectTargets,
  hasEditPermission,
}: any) => {
  return (
    <Form>
      <Box>
        <Typography variant="h4" style={{ marginBottom: 32 }}>
          {t(".header")}
        </Typography>

        <Grid container spacing={8}>
          <Grid item xs={12} md={6}>
            <Field
              component={Select}
              name="delegateId"
              label={t(".delegateName")}
              options={employeeOptions}
              fast
              required
              fullWidth
              disabled={!hasEditPermission}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Field
              component={Select}
              name="mode"
              label={t(".mode")}
              options={[
                {
                  label: t("client.enum.employeeDelegationMode.ALL"),
                  value: EnumEmployeeDelegationMode.ALL,
                },
                {
                  label: t("client.enum.employeeDelegationMode.CHOOSE"),
                  value: EnumEmployeeDelegationMode.CHOOSE,
                },
              ]}
              fast
              required
              fullWidth
              disableClearable
              disabled={!hasEditPermission}
            />
          </Grid>

          {isEnableSelectTargets && (
            <Grid item xs={12}>
              <Field
                fast
                component={Select}
                name="targets"
                label={t(".targets")}
                options={targetOptions}
                fullWidth
                multiple
                disabled={!hasEditPermission}
              />
            </Grid>
          )}

          {hasEditPermission && (
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-start" mt={4}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  style={{ marginRight: 16 }}
                >
                  {t(".cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!enabledSubmitButton}
                >
                  {t(".save")}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Form>
  );
};

const API = {
  FETCH_EMPLOYEES: gql`
    query FETCH_EMPLOYEES {
      getEmployees {
        id
        employeeCode
        firstName
        lastName
      }
    }
  `,
};

const enhancer = compose(
  withAuthorize({
    permissions: [PERMISSIONS.AGENT_MANAGEMENT_EDIT],
  }),
  withTranslation({
    prefix: "pages.main.employeeDelegation.components.EmployeeDelegationForm",
  }),
  withHooks((props: any, hooks: any) => {
    const { useCallback, useMemo, useQuery } = hooks;
    const {
      isAllMode,
      selectedDelegateId,
      isSubmitting,
      hasPermission,
    } = props;

    const { data } = useQuery(API.FETCH_EMPLOYEES);

    const employeeOptions = useMemo(() => {
      if (!data) return [];

      return data.getEmployees.map((employee: any) => ({
        label: `[${employee.employeeCode}] ${employee.firstName} ${employee.lastName}`,
        value: employee.id,
      }));
    }, [data]);

    const targetOptions = useMemo(() => {
      if (!data) return [];
      return data.getEmployees
        .filter((employee: any) => employee.id !== selectedDelegateId)
        .map((employee: any) => ({
          label: `[${employee.employeeCode}] ${employee.firstName} ${employee.lastName}`,
          value: employee.id,
        }));
    }, [data, selectedDelegateId]);

    const handleCancel = useCallback(() => {
      paths.employeeDelegationPath().push();
    }, []);

    const enabledSubmitButton = useMemo(() => {
      return !isSubmitting;
    }, [isSubmitting]);

    const isEnableSelectTargets = useMemo(() => {
      return !isAllMode;
    }, [isAllMode]);

    const hasEditPermission = hasPermission([
      PERMISSIONS.AGENT_MANAGEMENT_EDIT,
    ]);

    return {
      employeeOptions,
      targetOptions,
      handleCancel,
      enabledSubmitButton,
      isEnableSelectTargets,
      hasEditPermission,
    };
  })
);

export const EmployeeDelegationForm = enhancer(EmployeeDelegationFormComponent);
